import { useEffect, useMemo, useState } from "react";
import UnsplashHero from "../components/UnsplashHero";
import { HelpCircle, Search, ChevronDown, ArrowRight, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

/* Public FAQ page: searchable list of owner-curated answers. When no answer
   matches, the visitor can request one — an answer is drafted in the
   background for moderator review. */

const md = {
  a: ({ href, children }) => href?.startsWith("/")
    ? <Link to={href} className="text-blue-700 font-600 underline">{children}</Link>
    : <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 font-600 underline">{children}</a>,
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>,
};

export default function Faq() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/faq").then((r) => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // SEO: title, meta description and FAQPage JSON-LD so Google can index every answer
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Travel FAQ — Airport Security, Baggage, Delays & More | TravelerPulse.pro";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "description"); document.head.appendChild(meta); }
    meta.setAttribute("content", "Expert answers to the most-asked travel questions: airport security rules, TSA PreCheck, lounges, missed flights, compensation, baggage, layovers and connections.");
    return () => { document.title = prevTitle; if (prevDesc) meta.setAttribute("content", prevDesc); };
  }, []);

  useEffect(() => {
    if (!items.length) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-jsonld";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((i) => ({
        "@type": "Question",
        name: i.question,
        acceptedAnswer: { "@type": "Answer", text: i.answer.replace(/[*_#>]/g, "").slice(0, 1500) },
      })),
    });
    document.head.appendChild(el);
    // deep link: /faq#<id> auto-expands and scrolls to that answer
    const hash = window.location.hash.slice(1);
    if (hash && items.some((i) => i.id === hash)) {
      setOpenId(hash);
      setTimeout(() => document.querySelector(`[data-testid="faq-item-${hash}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
    return () => { document.getElementById("faq-jsonld")?.remove(); };
  }, [items]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((i) => i.question.toLowerCase().includes(t) || i.answer.toLowerCase().includes(t));
  }, [items, q]);

  const CAT_ORDER = ["Airport Basics", "Security & Programs", "Flights & Disruptions", "Baggage",
    "Documents & Visas", "Booking & Tickets", "Travel Tips", "General"];
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => { const c = i.category || "General"; (g[c] = g[c] || []).push(i); });
    return CAT_ORDER.filter((c) => g[c]?.length).map((c) => [c, g[c]])
      .concat(Object.keys(g).filter((c) => !CAT_ORDER.includes(c)).map((c) => [c, g[c]]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const toggle = (id) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    window.history.replaceState(null, "", next ? `#${next}` : window.location.pathname);
    if (next) api.post(`/faq/${id}/view`).catch(() => {});
  };

  const requestAnswer = async () => {
    if (q.trim().length < 8) { toast.error("Type your full question first (at least 8 characters)"); return; }
    setBusy(true);
    try {
      await api.post("/faq/request", { question: q.trim() });
      setRequested(true);
      toast.success("Thanks! We're preparing an answer — check back soon.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit — try again"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="faq-page">
      <UnsplashHero
        query="travel information counter airport"
        eyebrow="Travel FAQ"
        title="Frequently Asked Questions"
        subtitle="Practical answers to the travel questions we hear most — security, connections, lounges, compensation, layovers and more."
        testid="faq-hero"
      />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
        <p className="text-muted-foreground mt-1 mb-8 max-w-2xl">
          Can't find your question? Ask below and we'll build the answer.
        </p>

      {/* Featured: interactive packing checklist */}
      <Link to="/packing" data-testid="faq-packing-banner"
        className="flex items-center justify-between gap-3 border-2 border-[#0e7490] bg-cyan-50/70 px-5 py-4 mb-6 group hover:bg-cyan-50 transition-colors">
        <div>
          <div className="font-heading font-900 text-[#0e7490]">Universal Travel Packing Checklist — Interactive</div>
          <p className="text-sm text-zinc-700 mt-0.5">Pick your trip type, check items off as you pack, and save a running list for every trip.</p>
        </div>
        <ArrowRight className="w-6 h-6 text-[#0e7490] shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      {/* Search bar */}
      <div className="relative mb-8">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setRequested(false); }}
          placeholder="Search the FAQ — e.g. lounge access, missed flight, liquids…"
          data-testid="faq-search-input"
          className="w-full border-2 border-zinc-300 pl-12 pr-4 py-4 text-base outline-none focus:border-violet-600 transition-colors" />
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading answers…</p>
      ) : filtered.length > 0 ? (
        <div className="space-y-8" data-testid="faq-list">
          {grouped.map(([cat, list]) => (
            <section key={cat} data-testid={`faq-section-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="font-heading font-900 text-lg text-violet-800 border-b-2 border-violet-200 pb-2 mb-3">{cat}</h2>
              <div className="space-y-2">
                {list.map((i) => (
                  <div key={i.id} className="border border-zinc-300 bg-white" data-testid={`faq-item-${i.id}`}>
                    <button onClick={() => toggle(i.id)}
                      data-testid={`faq-question-${i.id}`}
                      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-violet-50 transition-colors">
                      <span className="font-heading font-700 text-base sm:text-lg">{i.question}</span>
                      <ChevronDown className={`w-5 h-5 shrink-0 text-violet-700 transition-transform ${openId === i.id ? "rotate-180" : ""}`} />
                    </button>
                    {openId === i.id && (
                      <div className="px-5 pb-5 pt-1 text-sm text-zinc-800 border-t border-zinc-100" data-testid={`faq-answer-${i.id}`}>
                        <ReactMarkdown components={md}>{i.answer}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-8 text-center" data-testid="faq-no-results">
          <p className="font-heading font-700 text-lg mb-2">No answer for that yet</p>
          {requested ? (
            <p className="text-sm text-emerald-700 font-600" data-testid="faq-request-confirmed">
              Got it — our team is preparing an answer. Check back soon!
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Ask it and we'll research and publish the answer — usually within a day.
              </p>
              <button onClick={requestAnswer} disabled={busy} data-testid="faq-request-btn"
                className="inline-flex items-center gap-2 bg-violet-700 text-white px-5 py-3 text-sm font-700 hover:bg-violet-600 disabled:opacity-50">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Request this answer"}
              </button>
            </>
          )}
          <div className="mt-4">
            <a href={`https://www.google.com/search?q=${encodeURIComponent(q)}`} target="_blank" rel="noreferrer"
              data-testid="faq-google-link"
              className="inline-flex items-center gap-1.5 text-sm font-700 text-emerald-700 hover:underline">
              <ArrowRight className="w-4 h-4" /> Search this on Google meanwhile
            </a>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
